import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChatType } from '../../models/chat-type';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  AssistantRequestModel,
  AssistantService,
  CompanyChatRequestModel,
  GenericChatRequestModel,
  MessageRole,
} from 'digiteq-ai-portal-client-lib';
import { AppSettingsService } from '../../services/app-settings.service';
import { get_encoding, Tiktoken } from 'tiktoken';
import { FeMessageModel } from '../../models/fe-message.model';
import { AiAssistantService } from '../../services/ai-assistant.service';
import { forkJoin, Observable } from 'rxjs';
import { CompanyChatService } from '../../company-chat.service';
import { StreamChatService } from '../../services/stream-chat.service';
import { ChatViewService } from '../../chat-view.service';
import { appConstants } from '../../constants/app-constants';
import { ContactUsService } from '../../services/contact-us.service';

@Component({
  selector: 'app-chat-input',
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss'
})
export class ChatInputComponent implements OnInit, OnDestroy {
  @ViewChild("textArea") textArea: ElementRef<HTMLElement>;

  assistantForm: FormGroup;
  tokenEncoding: Tiktoken;
  messageFileItems: File[] = [];
  messageCodeInterpreterFiles: File[] = [];
  enterKeyDown = false;
  displayFilesDialog = false;

  readonly CHART_TYPE = ChatType
  readonly appConstants = appConstants;

  get chatType(): ChatType {
    return this.chatViewService.chatType;
  }

  get messages(): FeMessageModel[] {
    return this.chatViewService.messages;
  }

  get aiTypingResponse(): boolean {
    return this.chatViewService.aiTypingResponse;
  }

  get hasInputValueErrors() {
    return (this.messageForm.hasError('minlength') || this.messageForm.hasError('whitespace'))
      && this.enterKeyDown && this.messageForm.value;
  }

  get messageForm() {
    return this.chatViewService.messageForm;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly settingsService: AppSettingsService,
    private readonly aiAssistantService: AiAssistantService,
    private readonly chatService: StreamChatService,
    private readonly assistantService: AssistantService,
    private readonly daiseService: CompanyChatService,
    private readonly chatViewService: ChatViewService,
    private readonly contactUsService: ContactUsService,
  ) {
    this.tokenEncoding = get_encoding('cl100k_base');
  }

  ngOnInit(): void {
    forkJoin([
      this.settingsService.getModels(),
      this.settingsService.getInitialSettings()
    ]).subscribe({
        next: ([models, settings]) => {
          this.settingsService.models = models;

          // Check if the model from settings exists in the models list
          const modelExists = models.some(model => model.id === settings.deploymentId && model.enabled === true);

          if (modelExists) {
            this.settingsService.selectModel(settings.deploymentId);
          } else {
            // Select the default model if the saved model doesn't exist
            const defaultModel = models.find(model => model.default === true);
            if (defaultModel) {
              this.settingsService.selectModel(defaultModel.id);
            }
          }
          this.initForm();
      setTimeout(() => {
        this.adaptTextAreaHeightToTheContent();
      }, 100);
        }
      });

    this.chatViewService.resend$.subscribe(() => {
      this.send(true);
    })
  }

  ngOnDestroy(): void {
    this.contactUsService.showContactUsIconInApp = true;
    this.contactUsService.showContactUsIconInChatInput = false;
  }

  isAssistant() {
    return this.chatViewService.isAssistant();
  }

  private notOnlyWhitespace(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value !== null && control.value.trim() === '') {
        return {'whitespace': true};
      }
      return null;
    };
  }


  private initForm() {
    this.chatViewService.messageForm = new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      this.notOnlyWhitespace(),
      this.messagesExceedMaxNumberOfToken()
    ]);
    if (this.isAssistant()) {
      this.assistantForm = this.fb.group({
        threadId: [null, Validators.required],
        message: ['', Validators.required],
        indexId: [null, Validators.required],
      });
    }
  }

  adaptTextAreaHeightToTheContent() {
    //pInputTextarea autoresize does not show scrollbar when input is too height
    this.textArea?.nativeElement.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = (this.scrollHeight + 5) + 'px';
    });
  }

  private messagesExceedMaxNumberOfToken(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let maxTokens = this.settingsService.selectedModel.maxInputTokens;

      if (this.getMessagesTokenLength(control.value) > maxTokens) {
        return {'maxNumOfTokens': true};
      }
      return null;
    };
  }

  getMessagesTokenLength(currentMessage: string) {
    const messagesHistoryString = this.messages.map(m => m.message).join('');
    const tokens = this.tokenEncoding.encode(currentMessage + messagesHistoryString);
    return tokens.length;
  }

  send(resend?: boolean) {
    this.enterKeyDown = true;

    if (!this.messageForm.valid && !resend) return;

    let message = this.messageForm.value;
    if (resend) {
      const index = this.chatViewService.stopped ? 2 : 1;
      if (this.messages.length - index > 0) {
        message = this.messages[this.messages.length - index].message;
        this.messages.splice(this.messages.length - index);
      }
    }

    this.enterKeyDown = false;
    this.chatViewService.aiResponseError = false;
    this.chatViewService.stopped = false;

    let assistantChat: AssistantRequestModel;
    if (this.isAssistant()) {
      this.assistantForm?.controls['threadId'].setValue(this.aiAssistantService.threadId);
      this.assistantForm?.controls['message'].setValue(message);
      this.chatViewService.messageFileItems = this.messageFileItems
      this.chatViewService.messageCodeInterpreterFiles = this.messageCodeInterpreterFiles
      assistantChat = this.assistantForm.value;
    }

    if (message) {
      this.chatViewService.addMessage({
        message,
        role: MessageRole.User,
      });
    }

    const lastMessages = this.settingsService.savedSettings?.lastMessages || 5;
    const chat: GenericChatRequestModel | CompanyChatRequestModel = {
      messages: this.messages.slice(-lastMessages),
      chatId: this.chatViewService.currentChatId as string
    };
    this.chatViewService.messageSent$.next();
    this.chatViewService.aiTypingResponse = true;
    this.textArea.nativeElement.style.height = '';

    this.chatViewService.currentChatName = undefined;

    this.sendChatMsg(chat, assistantChat, this.messageFileItems, this.messageCodeInterpreterFiles);
    this.messageForm.reset();
    this.assistantForm.reset();
    this.messageFileItems = []
    this.messageCodeInterpreterFiles = []
    this.chatViewService.images = [];
  }

  sendChatMsg(chat: GenericChatRequestModel | CompanyChatRequestModel, assistantChat: AssistantRequestModel, searchFiles?: Array<File>, interpreters?: Array<File>) {
    let chatStreamObservable$: Observable<any>;

    // refactor
    switch (this.chatType) {
      case ChatType.GENERIC: {
        (chat as GenericChatRequestModel).settings = this.settingsService.savedSettings;
        chatStreamObservable$ = this.chatService.realGenericChatStream(chat);
      }
        break;
      case ChatType.COMPANY: {
        (chat as CompanyChatRequestModel).indexId = this.daiseService.daiseIndex.id;
        chatStreamObservable$ = this.chatService.realCompanyChatStream(chat);
      }
        break;
      case ChatType.ASSISTANT: {
        chatStreamObservable$ = this.chatService.realAssistantChatStream(assistantChat, searchFiles, interpreters);
        break;
      }
    }

    let firstMessage = true;

    chatStreamObservable$.subscribe({
      next: response => {
        this.chatViewService.handleChatResponse(response, firstMessage);
        firstMessage = false;
        this.messageForm.reset();
      },
      error: err => {
        this.chatViewService.handleChatError(err)
      },
      complete: () => {
        this.chatViewService.aiTypingResponse = false;
        this.chatViewService.onChatResponseComplete$.next();
      },
    });
  }

  stop() {
    if (!this.chatViewService.currentChatId) {
      return;
    }
    const stopChatObservable$ = !this.isAssistant()
      ? this.chatService.stopChat(this.chatViewService.currentChatId as string)
      : this.assistantService.stopAssistantChat(+this.chatViewService.currentChatId);
    stopChatObservable$.subscribe(() => {
      this.chatViewService.handleChatStop()
    });

  }

  showFilesDialog() {
    this.displayFilesDialog = true;
  }

  handleActionButton() {
    if (this.aiTypingResponse) {
      this.stop();
    } else {
      this.send();
    }
  }

  updateFiles(files: File[]) {
    this.messageFileItems = files;
  }

  updateCodeInterpreterFiles(files: File[]) {
    this.messageCodeInterpreterFiles = files;
  }

  deleteAllFiles() {
    this.messageFileItems = [];
    this.messageCodeInterpreterFiles = [];
    this.displayFilesDialog = false;
  }

  goToAboutPage() {
    const url = ` https://dqwiki.skoda.vwgroup.com/display/AIPortal`;
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  openReportIssueDialog() {
    this.contactUsService.showContactUsDialog = true;
  }

  get showContactUsIcon() {
    return this.contactUsService.showContactUsIconInChatInput;
  }

  protected onSelectedFiles(messageFileItems: File[]) {
      this.messageFileItems = messageFileItems;
    }
}
